import type { ReactNode } from "react";
import { ArrowLeft, RefreshCw, ShieldX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { WorkspaceKey } from "./api";
import { useWorkspaceAccess } from "./useWorkspaceAccess";

export function WorkspaceAccessGuard({
  workspace,
  children,
}: {
  workspace: WorkspaceKey;
  children: ReactNode;
}) {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation("pages");
  const query = useWorkspaceAccess(
    projectId && projectId !== "new" ? projectId : undefined
  );

  if (projectId === "new") return children;

  if (!projectId || query.isPending) {
    return (
      <div
        className="mx-auto w-full max-w-5xl space-y-4 px-3 py-8"
        aria-label={t("project.workspace_access.loading")}
      >
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[28rem] w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <WorkspaceAccessMessage
        title={t("project.workspace_access.error_title")}
        description={t("project.workspace_access.error_description")}
        projectId={projectId}
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => query.refetch()}
          >
            <RefreshCw className="mr-2 size-4" aria-hidden="true" />
            {t("project.workspace_access.retry")}
          </Button>
        }
      />
    );
  }

  if (!query.canAccess(workspace)) {
    return (
      <WorkspaceAccessMessage
        title={t("project.workspace_access.denied_title")}
        description={t("project.workspace_access.denied_description")}
        projectId={projectId}
      />
    );
  }

  return children;
}

function WorkspaceAccessMessage({
  title,
  description,
  projectId,
  action,
}: {
  title: string;
  description: string;
  projectId: string;
  action?: ReactNode;
}) {
  const { t } = useTranslation("pages");
  return (
    <main className="mx-auto flex min-h-[calc(100svh-8rem)] w-full max-w-3xl items-center px-3 py-10">
      <section className="w-full rounded-lg border bg-card p-6 shadow-[var(--shadow-border)] sm:p-8">
        <span className="flex size-11 items-center justify-center rounded-lg border bg-background">
          <ShieldX className="size-5" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-balance text-2xl font-semibold tracking-tight">
          {title}
        </h1>
        <p className="mt-2 max-w-xl text-pretty text-sm leading-6 text-muted-foreground">
          {description}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to={`/projects/${projectId}`}>
              <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
              {t("project.workspace_access.back")}
            </Link>
          </Button>
          {action}
        </div>
      </section>
    </main>
  );
}
